import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../utils/invoiceHelpers';
import creditMemoService from '../../services/api/creditMemoService';

/**
 * CreditMemoDetailsModal Component
 *
 * Modal for viewing credit memo details and performing actions:
 * - Redeem: Customer receives cash/check refund (partial or full)
 * - Void: Only for credits issued in error with no applications
 *
 * @dependencies
 * - creditMemoService: API client for credit operations
 */
const CreditMemoDetailsModal = ({
  isOpen,
  onClose,
  creditMemo: initialCreditMemo,
  canVoid = false,
  onVoidSuccess,
  onRedeemSuccess
}) => {
  const [creditMemo, setCreditMemo] = useState(initialCreditMemo);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Action states
  const [activeAction, setActiveAction] = useState(null); // 'redeem' | 'void' | null
  const [isProcessing, setIsProcessing] = useState(false);

  // Redeem form state
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemMethod, setRedeemMethod] = useState('check');
  const [redeemReason, setRedeemReason] = useState('');
  const [redeemReference, setRedeemReference] = useState('');

  // Void form state
  const [voidReason, setVoidReason] = useState('');

  // Load credit memo details callback (defined before useEffect that uses it)
  // Uses optional chaining in dependency array since it's evaluated before early return
  const loadCreditMemoDetails = useCallback(async () => {
    if (!initialCreditMemo?.id) return;

    try {
      setIsLoading(true);
      const result = await creditMemoService.getCreditMemoById(initialCreditMemo.id);
      if (result.success) {
        setCreditMemo(result.creditMemo);
      }
    } catch (err) {
      console.error('Error loading credit memo details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [initialCreditMemo?.id]);

  // Load full credit memo details
  useEffect(() => {
    if (isOpen && initialCreditMemo?.id) {
      loadCreditMemoDetails();
    }
  }, [isOpen, initialCreditMemo?.id, loadCreditMemoDetails]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveAction(null);
      setError('');
      setRedeemAmount('');
      setRedeemMethod('check');
      setRedeemReason('');
      setRedeemReference('');
      setVoidReason('');
    }
  }, [isOpen]);

  // Set default redeem amount when opening redeem form
  useEffect(() => {
    if (activeAction === 'redeem' && creditMemo?.remainingBalanceCents > 0) {
      setRedeemAmount((creditMemo.remainingBalanceCents / 100).toFixed(2));
    }
  }, [activeAction, creditMemo?.remainingBalanceCents]);

  const handleRedeem = async () => {
    const amountCents = Math.round(parseFloat(redeemAmount) * 100);

    if (!amountCents || amountCents <= 0) {
      setError('Please enter a valid redemption amount');
      return;
    }
    if (amountCents > creditMemo.remainingBalanceCents) {
      setError(`Amount cannot exceed remaining balance of ${formatCurrency(creditMemo.remainingBalanceCents / 100)}`);
      return;
    }
    if (!redeemReason.trim()) {
      setError('Please enter a reason for this redemption');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const result = await creditMemoService.redeemCredit(creditMemo.id, {
        amountCents,
        redemptionMethod: redeemMethod,
        reason: redeemReason.trim(),
        referenceNumber: redeemReference.trim() || null
      });

      if (result.success) {
        onRedeemSuccess && onRedeemSuccess(result);
        onVoidSuccess && onVoidSuccess(result); // Also trigger void success for refresh
      } else {
        setError(result.error || 'Failed to redeem credit');
      }
    } catch (err) {
      console.error('Error redeeming credit:', err);
      setError(err.response?.data?.error || 'Failed to redeem credit');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      setError('Please enter a reason for voiding');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const result = await creditMemoService.voidCreditMemo(creditMemo.id, voidReason.trim());

      if (result.success) {
        onVoidSuccess && onVoidSuccess(result);
      } else {
        setError(result.error || 'Failed to void credit memo');
      }
    } catch (err) {
      console.error('Error voiding credit memo:', err);
      setError(err.response?.data?.error || 'Failed to void credit memo');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      partially_applied: 'bg-yellow-100 text-yellow-800',
      applied: 'bg-blue-100 text-blue-800',
      redeemed: 'bg-purple-100 text-purple-800',
      void: 'bg-red-100 text-red-800'
    };

    const statusLabels = {
      active: 'Active',
      partially_applied: 'Partially Used',
      applied: 'Fully Applied',
      redeemed: 'Redeemed',
      void: 'Void'
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Early return if modal closed or no data (matches RefundModal pattern)
  if (!isOpen || !initialCreditMemo) return null;

  // Determine available actions
  const hasRemainingBalance = creditMemo?.remainingBalanceCents > 0;
  const hasNoApplications = !creditMemo?.applications || creditMemo.applications.length === 0;
  const isActive = creditMemo?.status === 'active' || creditMemo?.status === 'partially_applied';

  // Can redeem if there's remaining balance and credit is active
  const canRedeem = canVoid && hasRemainingBalance && isActive;
  // Can void only if no applications have been made (issued in error)
  const canVoidCredit = canVoid && hasNoApplications && creditMemo?.status === 'active';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Credit Memo {creditMemo?.creditMemoNumber}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Created {formatDate(creditMemo?.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(creditMemo?.status)}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Main Details */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Customer */}
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium text-gray-900">
                  {creditMemo?.customerName || '-'}
                </p>
                {creditMemo?.customerEmail && (
                  <p className="text-sm text-gray-500">{creditMemo.customerEmail}</p>
                )}
              </div>

              {/* Source */}
              <div>
                <p className="text-sm text-gray-500">Source</p>
                <p className="font-medium text-gray-900 capitalize">
                  {creditMemo?.sourceType?.replace('_', ' ') || 'Manual'}
                </p>
                {creditMemo?.originalInvoiceNumber && (
                  <p className="text-sm text-gray-500">
                    From invoice {creditMemo.originalInvoiceNumber}
                  </p>
                )}
              </div>

              {/* Amount */}
              <div>
                <p className="text-sm text-gray-500">Original Amount</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(creditMemo?.amountCents / 100)}
                </p>
              </div>

              {/* Remaining */}
              <div>
                <p className="text-sm text-gray-500">Remaining Balance</p>
                <p className={`text-xl font-bold ${
                  creditMemo?.remainingBalanceCents > 0 ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {formatCurrency(creditMemo?.remainingBalanceCents / 100)}
                </p>
              </div>
            </div>

            {/* Reason */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Reason</p>
              <p className="text-gray-900">{creditMemo?.reason || '-'}</p>
              {creditMemo?.reasonCode && (
                <span className="inline-block mt-2 px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded capitalize">
                  {creditMemo.reasonCode.replace('_', ' ')}
                </span>
              )}
            </div>

            {/* Notes */}
            {creditMemo?.notes && (
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">Customer Notes</p>
                <p className="text-gray-900 bg-blue-50 p-3 rounded-lg">
                  {creditMemo.notes}
                </p>
              </div>
            )}

            {creditMemo?.internalNotes && (
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">Internal Notes</p>
                <p className="text-gray-900 bg-yellow-50 p-3 rounded-lg">
                  {creditMemo.internalNotes}
                </p>
              </div>
            )}

            {/* Applications / History */}
            {creditMemo?.applications && creditMemo.applications.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Usage History</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {creditMemo.applications.map((app, index) => (
                        <tr key={app.id || index}>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              app.applicationType === 'redemption'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {app.applicationType === 'redemption' ? 'Redeemed' : 'Applied'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-medium">
                            {formatCurrency(app.amountCents / 100)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {formatShortDate(app.appliedAt)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {app.invoiceNumber
                              ? `Invoice ${app.invoiceNumber}`
                              : app.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Void Information */}
            {creditMemo?.status === 'void' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-1">Voided</p>
                <p className="text-sm text-red-700">{creditMemo.voidReason}</p>
                <p className="text-xs text-red-600 mt-2">
                  Voided on {formatDate(creditMemo.voidedAt)}
                </p>
              </div>
            )}

            {/* Redeem Form */}
            {activeAction === 'redeem' && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm font-medium text-purple-800 mb-3">
                  Redeem Credit (Issue Refund to Customer)
                </p>
                <p className="text-xs text-purple-600 mb-4">
                  Use this when the customer receives cash, check, or bank transfer instead of applying to an invoice.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount to Redeem
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={creditMemo?.remainingBalanceCents / 100}
                        value={redeemAmount}
                        onChange={(e) => setRedeemAmount(e.target.value)}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Max: {formatCurrency(creditMemo?.remainingBalanceCents / 100)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Redemption Method
                    </label>
                    <select
                      value={redeemMethod}
                      onChange={(e) => setRedeemMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="check">Check</option>
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={redeemReference}
                    onChange={(e) => setRedeemReference(e.target.value)}
                    placeholder="Check number, transaction ID, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Redemption
                  </label>
                  <textarea
                    value={redeemReason}
                    onChange={(e) => setRedeemReason(e.target.value)}
                    placeholder="e.g., Customer requested refund via check"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setActiveAction(null);
                      setError('');
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRedeem}
                    className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    disabled={isProcessing || !redeemAmount || !redeemReason.trim()}
                  >
                    {isProcessing ? 'Processing...' : `Redeem ${redeemAmount ? formatCurrency(parseFloat(redeemAmount)) : ''}`}
                  </button>
                </div>
              </div>
            )}

            {/* Void Form */}
            {activeAction === 'void' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-2">
                  Void Credit Memo
                </p>
                <p className="text-xs text-red-600 mb-3">
                  Only use this if the credit was issued in error. This will remove the entire remaining balance.
                </p>
                <textarea
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Enter reason for voiding (e.g., Credit issued in error)"
                  rows={2}
                  className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-3"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setActiveAction(null);
                      setVoidReason('');
                      setError('');
                    }}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVoid}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    disabled={isProcessing || !voidReason.trim()}
                  >
                    {isProcessing ? 'Voiding...' : 'Confirm Void'}
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex gap-2">
                {canRedeem && !activeAction && (
                  <button
                    onClick={() => setActiveAction('redeem')}
                    className="px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Redeem Credit
                  </button>
                )}
                {canVoidCredit && !activeAction && (
                  <button
                    onClick={() => setActiveAction('void')}
                    className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm"
                  >
                    Void (Issued in Error)
                  </button>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreditMemoDetailsModal;
